import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import { DevourPlayApi, Token } from "@devour/client";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

export interface QuestProgress {
    questId: string;
    questName: string;
    progressMade: number;
    totalRequired: number;
}

export interface Milestone {
    type: string;
    description: string;
    xpAwarded?: number;
}

export interface SessionInfo {
    sessionId?: string;
    gameId: string;
    gameTitle: string;
    gameThumbnail?: string;
    lastPlayed: string;
    sessionDuration: number;
    xpEarned: number;
    fuelEarned: number;
    questsCompleted: number;
    questProgress: QuestProgress[];
    milestonesReached: Milestone[];
    averageComparison?: {
        duration: "above" | "below" | "average";
        xpEarned: "above" | "below" | "average";
        percentDiff: number;
    };
}

export interface RecentSession {
    sessionId: string;
    gameId: string;
    gameTitle: string;
    gameThumbnail?: string;
    duration: number;
    xpEarned: number;
    questsCompleted: number;
    timestamp: string;
}

export interface LastSessionResponse {
    lastSession: SessionInfo | null;
    recentSessions: RecentSession[];
    todaySummary: {
        totalXP: number;
        totalTime: number;
        uniqueGames: number;
    };
    userAverages: {
        sessionDuration: number;
        xpPerSession: number;
        questsPerSession: number;
    };
}

export const LAST_SESSION_QUERY_KEY = "devourplay-last-session";

async function fetchLastSession(fullToken: Token): Promise<LastSessionResponse> {
    return new DevourPlayApi(getConfig(fullToken)).getDevourplayLastSession() as unknown as LastSessionResponse;
}

export const useGetLastSession = () => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    const { data, isLoading, refetch, error } = useQuery<LastSessionResponse>({
        queryKey: [LAST_SESSION_QUERY_KEY, fullToken?.id],
        queryFn: () => fetchLastSession(fullToken!),
        staleTime: 10 * 1000, // Consider data fresh for 10 seconds - more responsive to updates
        enabled: !!fullToken?.token,
        refetchOnWindowFocus: false,
    });

    return {
        data: data || {
            lastSession: null,
            recentSessions: [],
            todaySummary: {
                totalXP: 0,
                totalTime: 0,
                uniqueGames: 0,
            },
            userAverages: {
                sessionDuration: 60,
                xpPerSession: 100,
                questsPerSession: 1,
            },
        },
        isLoading,
        error,
        refetch,
    };
};